import React, { useState, useRef } from 'react';
import api from '../api';

const TalentImportModal = ({ isOpen, onClose, onComplete }) => {
  const [step, setStep] = useState('upload');
  const [file, setFile] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [mode, setMode] = useState('import_and_update');
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  const reset = () => {
    setStep('upload'); setFile(null); setSheets([]); setSelectedSheet(''); setResult(null);
  };

  const handleClose = () => { reset(); onClose(); };

  const handleFile = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);

    const formData = new FormData();
    formData.append('file', f);
    formData.append('action', 'preview');

    try {
      const res = await api.post('/talent/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSheets(res.data.sheets || []);
      if (res.data.sheets.length > 0) setSelectedSheet(res.data.sheets[0].name);
      setStep('configure');
    } catch (err) {
      alert('Failed to read file: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleImport = async () => {
    if (!file || !selectedSheet) return;
    setImporting(true);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('sheet', selectedSheet);
    formData.append('mode', mode);

    try {
      const res = await api.post('/talent/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      setResult(res.data);
      setStep('done');
    } catch (err) {
      alert('Import failed: ' + (err.response?.data?.error || err.message));
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/talent/import-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'talent_import_template.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
    } catch (e) { alert('Failed to download template'); }
  };

  if (!isOpen) return null;

  const selectedSheetData = sheets.find(s => s.name === selectedSheet);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={handleClose}>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b border-zinc-800 flex justify-between items-center">
          <h2 className="text-lg font-bold text-white">Import Talent</h2>
          <button onClick={handleClose} className="text-zinc-400 hover:text-white text-xl">x</button>
        </div>

        <div className="p-6">
          {step === 'upload' && (
            <div className="space-y-4">
              <div className="text-zinc-400 text-sm">Upload a roster Excel (.xlsx) to bulk import talent into your network.</div>

              <div
                onClick={() => fileRef.current?.click()}
                className="border-2 border-dashed border-zinc-700 rounded-xl p-8 text-center cursor-pointer hover:border-zinc-500 transition-colors"
              >
                <div className="text-zinc-400 text-sm">Click to upload .xlsx file</div>
                <div className="text-zinc-600 text-xs mt-1">Max 10MB</div>
                <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleFile} />
              </div>

              <button onClick={downloadTemplate} className="w-full bg-zinc-800 border border-zinc-700 text-white px-4 py-2 rounded-lg text-sm hover:border-zinc-600">
                Download Import Template
              </button>
            </div>
          )}

          {step === 'configure' && (
            <div className="space-y-4">
              <div className="text-zinc-400 text-sm">File: <span className="text-white">{file?.name}</span></div>

              <div>
                <label className="block text-xs text-zinc-400 mb-1">Select Sheet</label>
                <select value={selectedSheet} onChange={e => setSelectedSheet(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
                  {sheets.map(s => <option key={s.name} value={s.name}>{s.name} ({s.rowCount} rows)</option>)}
                </select>
              </div>

              {selectedSheetData && (
                <div className="bg-zinc-800 rounded-lg p-3">
                  <div className="text-zinc-400 text-xs mb-1">Detected columns ({selectedSheetData.headers.length})</div>
                  <div className="flex flex-wrap gap-1">
                    {selectedSheetData.headers.slice(0, 20).map(h => (
                      <span key={h} className="px-2 py-0.5 bg-zinc-700 text-zinc-300 rounded text-xs">{h}</span>
                    ))}
                    {selectedSheetData.headers.length > 20 && (
                      <span className="text-zinc-500 text-xs">+{selectedSheetData.headers.length - 20} more</span>
                    )}
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs text-zinc-400 mb-1">Import Mode</label>
                <select value={mode} onChange={e => setMode(e.target.value)}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
                  <option value="import_and_update">Import new + update existing (by name match)</option>
                  <option value="import_new_only">Import new only (skip duplicates)</option>
                  <option value="import_all_new">Import all as new (ignore duplicates)</option>
                </select>
              </div>

              <div className="flex gap-3">
                <button onClick={reset} className="flex-1 bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm">Back</button>
                <button onClick={handleImport} disabled={importing}
                  className="flex-1 bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                  {importing ? 'Importing...' : `Import ${selectedSheetData?.rowCount || 0} rows`}
                </button>
              </div>
            </div>
          )}

          {step === 'done' && result && (
            <div className="space-y-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-nextide-turquoise mb-2">Import Complete</div>
              </div>
              <div className="bg-zinc-800 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-zinc-400">Total rows processed</span><span className="text-white font-medium">{result.total}</span></div>
                <div className="flex justify-between text-sm"><span className="text-zinc-400">New creators imported</span><span className="text-green-400 font-medium">{result.imported}</span></div>
                <div className="flex justify-between text-sm"><span className="text-zinc-400">Existing creators updated</span><span className="text-blue-400 font-medium">{result.updated}</span></div>
                <div className="flex justify-between text-sm"><span className="text-zinc-400">Skipped</span><span className="text-zinc-500 font-medium">{result.skipped}</span></div>
              </div>
              <button onClick={() => { handleClose(); onComplete(); }}
                className="w-full bg-nextide-turquoise text-black px-4 py-3 rounded-lg font-semibold">
                Done
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TalentImportModal;
